import React, { useCallback, useEffect, useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import { getPlatformPayments } from '../../services/api';
import { formatDateTime, formatMoney } from '../../utils/formatters';

export const PAYMENT_STATUS_LABELS = { pending: 'Ожидает', succeeded: 'Оплачен', canceled: 'Отменён' };

/**
 * Лента платежей по всей платформе плюс денежная сводка. Фильтры и запрос
 * держит у себя: эти данные больше нигде на странице не нужны, а тянуть их
 * при каждой смене фильтра через родителя означало бы гонять состояние вверх-вниз.
 */
const PaymentsCard = ({ finance, orgs = [] }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [orgId, setOrgId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPlatformPayments({
        status: status || undefined,
        orgId: orgId ? Number(orgId) : undefined,
      });
      setPayments(data.payments || []);
      setError('');
    } catch (e) {
      setError('Не удалось загрузить платежи');
    } finally {
      setLoading(false);
    }
  }, [status, orgId]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      {finance && (
        <section className="ui-card">
          <div className="ui-card-head">
            <h2>Деньги</h2>
            <p>Выручка считается по подтверждённым платежам, а не по выставленным счетам.</p>
          </div>
          <div className="ui-metrics-grid">
            <div className="ui-metric-tile">
              <div className="ui-metric-value">{formatMoney(finance.revenue_month)}</div>
              <div className="ui-metric-label">Выручка в этом месяце</div>
            </div>
            <div className="ui-metric-tile">
              <div className="ui-metric-value">{formatMoney(finance.revenue_prev_month)}</div>
              <div className="ui-metric-label">Прошлый месяц</div>
            </div>
            <div className="ui-metric-tile">
              <div className="ui-metric-value">{formatMoney(finance.arpu)}</div>
              <div className="ui-metric-label">ARPU</div>
            </div>
            <div className="ui-metric-tile">
              <div className="ui-metric-value">{finance.payment_counts.pending}</div>
              <div className="ui-metric-label">Платежей в ожидании</div>
            </div>
            <div className="ui-metric-tile">
              <div className={`ui-metric-value ${finance.orgs_with_billing_failures ? 'ui-text-danger' : ''}`}>
                {finance.orgs_with_billing_failures}
              </div>
              <div className="ui-metric-label">Организаций с ошибкой списания</div>
            </div>
            <div className="ui-metric-tile">
              <div className="ui-metric-value">{formatMoney(finance.revenue_total)}</div>
              <div className="ui-metric-label">Выручка за всё время</div>
            </div>
          </div>
        </section>
      )}

      <section className="ui-card">
        <div className="ui-card-head platform-card-head-row">
          <h2>Платежи</h2>
          <div className="platform-filters">
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Все статусы</option>
              <option value="succeeded">Оплаченные</option>
              <option value="pending">В ожидании</option>
              <option value="canceled">Отменённые</option>
            </select>
            <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
              <option value="">Все организации</option>
              {orgs.map((org) => (
                <option value={org.id} key={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
        </div>

        {loading && <LoadingSpinner />}
        {!loading && error && <div className="ui-inline-error">{error}</div>}
        {!loading && !error && payments.length === 0 && (
          <div className="ui-empty">Платежей по этим условиям нет</div>
        )}

        {!loading && !error && payments.length > 0 && (
          <div className="platform-org-table">
            {payments.map((payment) => (
              <div className="platform-org-row" key={payment.id}>
                <div className="platform-org-main">
                  <div className="platform-org-name">{payment.org_name || '—'}</div>
                  <div className="ui-dim">
                    {payment.description || 'Подписка'} · создан {formatDateTime(payment.created_at)}
                    {payment.paid_at && ` · оплачен ${formatDateTime(payment.paid_at)}`}
                  </div>
                </div>
                <span className="platform-payment-amount">{formatMoney(payment.amount)}</span>
                <span className={`ui-status ${payment.status === 'succeeded' ? 'on' : 'off'}`}>
                  {PAYMENT_STATUS_LABELS[payment.status] || payment.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default PaymentsCard;
